import ChatBubble from "@amzn/awsui-chat-components/chat-bubble";
import { CopyToClipboard } from "@amzn/awsui-components-console";
import ExpandableSection from "@amzn/awsui-components-console/expandable-section";
import SpaceBetween from "@amzn/awsui-components-console/space-between";
import React, { memo } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import IconikExpandables from "../iconik/IconikExpandables";
import ChatAvatar from "./ChatAvatar";
import MetadataFiltersSummary from "./MetadataFiltersSummary";
import SearchContextSummary from "./SearchContextSummary";
import styles from "./styles.module.css";
import { AnyIconikItem, ChatMessage, NodeElement } from "./types";
import {
  parseColumnsAndItems,
  parseMetadataFilters,
  parseSearchContext,
  stripMetadataFiltersBlock,
  stripSearchContextBlock,
} from "./utils";

/**
 * Trusted domain patterns for links rendered in model responses.
 * Per GenAI-Sec Image/Link Markdown vulnerability guidance, only allow-listed
 * origins are permitted in <a> and <img> tags to prevent data exfiltration.
 *
 * Uses hostname suffix matching so patterns work across accounts, stages,
 * and subdomains (e.g., pvls-iconik-dev-test-bucket.s3-accelerate.amazonaws.com).
 */
const TRUSTED_HOSTNAME_PATTERNS: readonly string[] = [
  ".nba.com",
  ".wnba.com",
  ".amazon.com",
  ".amazonaws.com",
  ".iconik.io",
];

/** Exact hostnames that don't have a subdomain prefix. */
const TRUSTED_EXACT_HOSTNAMES: readonly string[] = [
  "nba.com",
  "wnba.com",
  "amazon.com",
  "app.iconik.io",
];

/**
 * Validates whether a URL belongs to a trusted domain.
 */
function isTrustedUrl(href: string | undefined): boolean {
  if (!href) return false;
  try {
    const { hostname } = new URL(href);
    return (
      TRUSTED_EXACT_HOSTNAMES.includes(hostname) ||
      TRUSTED_HOSTNAME_PATTERNS.some((pattern) => hostname.endsWith(pattern))
    );
  } catch {
    return false;
  }
}

interface ChatMessagesProps {
  messages: Array<ChatMessage>;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({ messages }) => (
  <div className="messages" role="region" aria-label="Chat">
    <SpaceBetween size="xs">
      {messages.map(({ author, content, timestamp }, index) => {
        // The agent embeds a machine-oriented [metadata_filters: {...}] block
        // in its answer text. Strip it from the rendered prose and surface the
        // same filters as a friendly summary instead of showing raw JSON.
        // Multi-sport answers have a [search_context: {...}] block instead.
        const isString = typeof content === "string";
        const filters = isString
          ? parseMetadataFilters(content as string)
          : undefined;
        const searchContext = isString
          ? parseSearchContext(content as string)
          : undefined;
        let displayContent = content;
        if (isString && filters) {
          displayContent = stripMetadataFiltersBlock(content as string);
        }
        if (isString && searchContext) {
          displayContent = stripSearchContextBlock(displayContent as string);
        }

        return (
        <ChatBubble
          key={`${author.type}-${timestamp}-${index}`}
          type={author.type === "gen-ai" ? "incoming" : "outgoing"}
          data-testid={
            author.type === "gen-ai" ? "incoming-message" : "outgoing-message"
          }
          avatar={<ChatAvatar {...author} />}
          showLoadingBar={author.loading}
          ariaLabel={`${author.type}-${timestamp}-${index}-bubble`}
          actions={
            author.type === "gen-ai" && !author.loading ? (
              <CopyToClipboard
                copyButtonAriaLabel="Copy message"
                copyErrorText="Message failed to copy"
                copySuccessText="Copied message"
                textToCopy={
                  typeof displayContent === "string"
                    ? displayContent
                    : (content as string)
                }
                variant="icon"
              />
            ) : undefined
          }
        >
          <SpaceBetween size="xs">
            <div
              key={`chat-bubble-${timestamp}-content` + timestamp + "content"}
            >
              {typeof displayContent === "string" ? (
                <div className={styles.markdown}>
                  <Markdown
                    components={{
                      a({ href, children, ...props }) {
                        if (!isTrustedUrl(href)) {
                          // Render as plain text for untrusted links
                          return <span>{children}</span>;
                        }
                        return (
                          <a
                            {...props}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {children}
                          </a>
                        );
                      },
                      img() {
                        // Block all images from model output to prevent
                        // data exfiltration via image URL requests.
                        return null;
                      },
                      table({ node, children }) {
                        try {
                          const { items, areIconikLinks } =
                            parseColumnsAndItems(
                              node as unknown as NodeElement
                            );

                          if (areIconikLinks) {
                            return (
                              <IconikExpandables
                                items={items as AnyIconikItem[]}
                                // Metadata filters live in the surrounding
                                // message text (not the table node), so this
                                // reuses the value already parsed once per
                                // message above rather than re-parsing here.
                                filters={filters}
                              />
                            );
                          }

                          // Non-Iconik tables: render natively in a
                          // collapsible section so inline formatting is
                          // preserved and long tables don't dominate the chat.
                          const headerText = `Results (${items.length} row${items.length !== 1 ? "s" : ""})`;

                          return (
                            <ExpandableSection
                              variant="footer"
                              headerText={headerText}
                              defaultExpanded
                            >
                              <table className={styles.markdownTable}>
                                {children}
                              </table>
                            </ExpandableSection>
                          );
                        } catch {
                          // Gracefully handle any unexpected table parsing errors
                          return null;
                        }
                      },
                    }}
                    remarkPlugins={[remarkGfm]}
                  >
                    {displayContent}
                  </Markdown>
                </div>
              ) : (
                content
              )}
            </div>
            {filters && <MetadataFiltersSummary filters={filters} />}
            {searchContext && <SearchContextSummary context={searchContext} />}
          </SpaceBetween>
        </ChatBubble>
        );
      })}
    </SpaceBetween>
  </div>
);

export default memo(ChatMessages);
